import React, { Fragment, useId, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import sales from '../../Sales';
import { useAdmin } from '../adminContext';
import { plural, sortByName } from '../format';
import { useAdminData } from '../store';
import { Avatar, Button, EntryCard, Field, FormError, ListState, SearchInput, useFreshIds } from '../ui';

const SellerForm = ({ seller, takenNames, onDone }) => {
  const isNew = !seller;
  const { notify, confirm, changed } = useAdmin();
  const id = useId();
  const [name, setName] = useState(seller?.name || '');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const submit = async () => {
    const clean = name.trim();
    if (!clean) {
      setProblem('Vul een naam in.');
      return;
    }
    if (takenNames.includes(clean.toLowerCase())) {
      setProblem(`Er is al een verkoper die ${clean} heet.`);
      return;
    }

    setBusy(true);
    setProblem('');
    try {
      if (isNew) {
        await sales.post('users', { name: clean, active: 1 });
        notify(`${clean} toegevoegd`);
        setName('');
      } else {
        await sales.put('users', { user_id: seller.user_id, name: clean });
        notify('Verkoper opgeslagen');
        onDone();
      }
      changed();
    } catch (error) {
      console.warn(error);
      setProblem('Opslaan is mislukt. Probeer het opnieuw.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const confirmed = await confirm({
      title: `${seller.name} verwijderen?`,
      text: `${seller.name} verdwijnt uit de lijst met verkopers en kan geen nieuwe deals meer krijgen. Dit kun je niet ongedaan maken.`,
    });
    if (!confirmed) return;
    setBusy(true);
    try {
      await sales.delete(`users?user_id=${seller.user_id}`);
      notify(`${seller.name} verwijderd`);
      changed();
      onDone();
    } catch (error) {
      console.warn(error);
      notify('Verwijderen is mislukt', 'error');
      setBusy(false);
    }
  };

  return (
    <EntryCard
      title={isNew ? 'Nieuwe verkoper' : 'Verkoper bewerken'}
      inline={!isNew}
      onSubmit={submit}
      onCancel={onDone}
      busy={busy}
    >
      <div className="adm-inline">
        <Field label="Naam, zoals op de TV" htmlFor={`${id}-name`} className="adm-grow">
          <input
            id={`${id}-name`}
            className="adm-input"
            type="text"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Voornaam"
          />
        </Field>
        {!isNew && (
          <Button variant="danger-text" icon={Trash2} onClick={remove} disabled={busy}>
            Verwijderen
          </Button>
        )}
        {!isNew && (
          <Button variant="ghost" onClick={onDone} disabled={busy}>
            Annuleren
          </Button>
        )}
        <Button type="submit" icon={isNew ? Plus : undefined} disabled={busy}>
          {busy ? 'Bezig...' : isNew ? 'Toevoegen' : 'Opslaan'}
        </Button>
      </div>
      <FormError>{problem}</FormError>
    </EntryCard>
  );
};

const Sellers = () => {
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const { data, loading, error, reload } = useAdminData({ users: 'users?filter=true', deals: 'deals' });

  const rows = useMemo(() => {
    if (!data) return [];
    const counts = new Map();
    data.deals.forEach((deal) => counts.set(deal.user_id, (counts.get(deal.user_id) || 0) + 1));
    return sortByName(data.users).map((user) => ({ ...user, count: counts.get(user.user_id) || 0 }));
  }, [data]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? rows.filter((row) => (row.name || '').toLowerCase().includes(needle)) : rows;
  }, [rows, query]);

  const fresh = useFreshIds(rows, (row) => row.user_id);

  const namesExcept = (userId) =>
    rows.filter((row) => row.user_id !== userId).map((row) => (row.name || '').toLowerCase());

  return (
    <>
      <SellerForm takenNames={namesExcept(null)} onDone={() => {}} />
      <section className="adm-panel">
        <div className="adm-panel-head">
          <h2 className="adm-panel-title">Verkopers</h2>
          {data && <span className="adm-count num">{plural(rows.length, 'verkoper', 'verkopers')}</span>}
          <SearchInput value={query} onChange={setQuery} placeholder="Zoek een verkoper" />
        </div>
        <ListState
          loading={loading && !data}
          error={error && !data}
          onRetry={reload}
          empty={shown.length === 0 && (query ? 'Geen verkoper gevonden.' : 'Nog geen verkopers.')}
        >
          <div className="adm-rows">
            {shown.map((row) => {
              const open = editing === row.user_id;
              return (
                <Fragment key={row.user_id}>
                  <button
                    type="button"
                    className={`adm-row adm-row-simple${open ? ' is-open' : ''}${fresh.has(row.user_id) ? ' is-fresh' : ''}`}
                    aria-expanded={open}
                    onClick={() => setEditing(open ? null : row.user_id)}
                  >
                    <Avatar name={row.name} />
                    <span className="adm-row-main">
                      <b>{row.name}</b>
                    </span>
                    <span className="adm-row-meta num">{plural(row.count, 'deal', 'deals')}</span>
                    <Pencil size={15} className="adm-row-edit" aria-label="Bewerken" />
                  </button>
                  {open && (
                    <div className="adm-row-editor">
                      <SellerForm seller={row} takenNames={namesExcept(row.user_id)} onDone={() => setEditing(null)} />
                    </div>
                  )}
                </Fragment>
              );
            })}
          </div>
        </ListState>
      </section>
    </>
  );
};

export default Sellers;
